/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import PreviouslyPurchasedBadge from "@eisinc/App/Components/CustomBadges/PreviouslyPurchasedBadge";
import ProductVideoAsset, { ProductVideoAssetStyles } from "@eisinc/App/Components/Media/ProductVideoAsset";
import setSelectedVideoIndex from "@eisinc/App/Handlers/Pages/ProductDetails/SetSelectedVideoIndex";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getSelectedVideo, getSelectedVideoIndex } from "@eisinc/App/Store/Selectors/ProductDetailsSelectors";
import { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import ProductImageCarousel, { ProductImageCarouselStyles } from "@eisinc/Overrides/Components/ProductImageCarousel";
import { XProductContextModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import Zoom from "@eisinc/XDL/Icons/Zoom";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Badge from "@insite/mobius/Badge";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedVideo: getSelectedVideo(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    selectedVideoIndex: getSelectedVideoIndex(state, ownProps.productContext.product),
    // XNG-Change
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
    setSelectedVideoIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Change
    magIcon?: IconPresentationProps;
    wrapper?: InjectableCss;
    verticalCarousel?: ProductImageCarouselStyles;
    imageContainer?: InjectableCss;
    modalImageContainer?: InjectableCss;
    videoAsset?: ProductVideoAssetStyles;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 70vh;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    // XNG-Change
    selectedVideo,
    selectedVideoIndex,
    setSelectedVideoIndex,
    isTabletScreen,
}: Props) => {
    const StyledWrapperX = getStyledWrapper("div") as any;

    const isImageAvailable = selectedImage && selectedImage.largeImagePath;

    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const { product } = productContext;

    const imageClickHandler = () => {
        if (isImageAvailable) {
            setFullImageModalIsOpen(true);
        }
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedVideoIndex({ index: -1, product });
        setSelectedImageIndex({ index });
    };

    const selectVideoHandler = (index: number) => {
        setSelectedVideoIndex({ index, product });
    };

    // XNG-Change - use isLoading from product context
    const isLoading = (productContext as XProductContextModel).isLoading;

    // XNG-Change add isLoading
    if (!product && !isLoading) {
        return null;
    }

    const video = selectedVideo;

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText || product.productTitle,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    const path = image.mediumImagePath || image.largeImagePath;

    // XNG-Change add isLoading
    if (!path && !isLoading) {
        return null;
    }

    const VideoSection = video?.videoId && (
        <ProductVideoAsset
            videoId={video.videoId}
            title={video.name || product.productTitle}
            description={video.description || product.productTitle}
            extendedStyles={styles.videoAsset}
        />
    );

    // XNG-Change Add carousel + wrapper
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapperX isImageAvailable={isImageAvailable}>
                {VideoSection}
                {!video && !showOverlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper}>
                        <ProductPrimaryImage
                            product={product}
                            onClick={imageClickHandler}
                            image={image}
                            extendedStyles={styles.primaryImage}
                            // XNG-Changes
                            useLargeImage={true}
                            {...{ isMagnifierEnabled: true }}
                        />
                        <IconMemo src={Zoom} {...styles.magIcon} onClick={imageClickHandler} />
                        {/* XNG-Changes */}
                        <PreviouslyPurchasedBadge product={product} />
                    </StyledWrapper>
                )}
                {!video && showOverlayBadge && overlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            onClick={imageClickHandler}
                            extendedStyles={styles.primaryImage}
                            // XNG-Changes
                            useLargeImage={true}
                            {...{ isMagnifierEnabled: true, isLoading }}
                        />
                        <IconMemo src={Zoom} {...styles.magIcon} onClick={imageClickHandler} />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                        {/* XNG-Changes */}
                        <PreviouslyPurchasedBadge product={product} />
                    </StyledWrapper>
                )}
            </StyledWrapperX>
            {/* XNG-Change: remove product.image check to add skeleton */}
            <ProductImageCarousel
                selectedIndex={selectedImageIndex}
                onSelectImage={selectImageHandler}
                // XNG-Change
                extendedStyles={isTabletScreen ? styles.imageCarousel : styles.verticalCarousel}
                numberOfVisibleThumbnails={6}
                scrollingBehavior={isTabletScreen ? "horizontal" : "vertical"}
                selectedVideoIndex={selectedVideoIndex}
                onSelectVideo={selectVideoHandler}
            />
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <StyledWrapper {...styles.modalImageContainer}>
                    {VideoSection}
                    {!video && (
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            useLargeImage={true}
                            extendedStyles={styles.fullPrimaryImage}
                        />
                    )}
                </StyledWrapper>
                {/* XNG-Change: not included in design */}
                {/* {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        selectedVideoIndex={selectedVideoIndex}
                        onSelectImage={selectImageHandler}
                        onSelectVideo={selectVideoHandler}
                        extendedStyles={styles.imageCarousel}
                        scrollingBehavior="horizontal"
                        numberOfVisibleThumbnails={8}
                    />
                )} */}
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
